import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Autocomplete,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

export default function OrderSupplierSelector({
  options,
  drafts,
  activeSupplierId,
  inputValue,
  loading,
  error,
  disabled,
  onInputChange,
  onSelect,
  onActivate,
  onRemove,
  onRetry,
}) {
  return (
    <Stack spacing={2.5} sx={{ minHeight: 0 }}>
      <Stack spacing={0.75}>
        <Typography component="h2" sx={{ color: "primary.900", fontSize: 18, fontWeight: 750 }}>
          Seleccionar proveedor
        </Typography>

        <Autocomplete
          value={null}
          inputValue={inputValue}
          options={options}
          loading={loading}
          disabled={disabled}
          filterOptions={(availableOptions) => availableOptions}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
          noOptionsText="No se encontraron proveedores"
          loadingText="Buscando proveedores..."
          clearText="Limpiar búsqueda"
          openText="Mostrar proveedores"
          closeText="Ocultar proveedores"
          onChange={(_, supplier) => supplier && onSelect(supplier)}
          onInputChange={(_, value, reason) => {
            if (reason !== "reset") onInputChange(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Buscar proveedor"
              size="small"
              inputProps={{
                ...params.inputProps,
                maxLength: 200,
                "aria-label": "Buscar proveedor para el pedido",
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    {loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <SearchIcon sx={{ color: "primary.900", fontSize: 18 }} />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  minHeight: 38,
                  borderRadius: "10px",
                  bgcolor: "neutral.50",
                  fontSize: 13,
                  fontWeight: 650,
                  "& fieldset": { borderColor: "neutral.700" },
                  "&:hover fieldset": { borderColor: "primary.900" },
                  "&.Mui-focused fieldset": { borderColor: "secondary.500" },
                },
              }}
            />
          )}
        />

        {error ? (
          <Alert
            severity="error"
            action={
              <ButtonBase onClick={onRetry} sx={{ fontSize: 12, fontWeight: 800 }}>
                Reintentar
              </ButtonBase>
            }
            sx={{ borderRadius: "14px", fontSize: 12, fontWeight: 650 }}
          >
            {error}
          </Alert>
        ) : null}
      </Stack>

      <Stack spacing={1} sx={{ minHeight: 0 }}>
        <Typography component="h2" sx={{ color: "primary.900", fontSize: 18, fontWeight: 750 }}>
          Proveedores seleccionados
        </Typography>

        {drafts.length ? (
          <Stack spacing={0.75} sx={{ overflow: "auto", pr: 0.5 }}>
            {drafts.map((draft) => {
              const active = Number(draft.supplierId) === Number(activeSupplierId);
              return (
                <ButtonBase
                  key={draft.supplierId}
                  onClick={() => onActivate(draft.supplierId)}
                  aria-pressed={active}
                  sx={{
                    width: "100%",
                    minHeight: 36,
                    px: 1.25,
                    border: "1px solid",
                    borderColor: active ? "secondary.500" : "secondary.300",
                    borderRadius: "8px",
                    bgcolor: active ? "secondary.200" : "neutral.50",
                    color: "primary.900",
                    justifyContent: "space-between",
                    textAlign: "left",
                    transition: "background-color .15s, border-color .15s",
                    "&:hover": { bgcolor: active ? "secondary.250" : "secondary.50" },
                  }}
                >
                  <Typography noWrap sx={{ minWidth: 0, fontSize: 13, fontWeight: active ? 800 : 700 }}>
                    {draft.supplierName}
                  </Typography>
                  <IconButton
                    component="span"
                    size="small"
                    aria-label={`Quitar proveedor ${draft.supplierName}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemove(draft.supplierId);
                    }}
                    sx={{ ml: 1, width: 25, height: 25, color: "primary.900" }}
                  >
                    <CloseIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </ButtonBase>
              );
            })}
          </Stack>
        ) : (
          <Typography sx={{ color: "neutral.600", fontSize: 13, fontWeight: 650 }}>
            Todavía no seleccionaste proveedores.
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
